import {
  AboveGradeWallType,
  DoorItemType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GlassItemType,
  PositionType
} from 'data/types';
import { exposureForOutwardNormal } from 'utils/edge-exposure';
import {
  clampOpeningOffsetOnRun,
  EMPTY_OPENING_RUN_BOUNDS,
  normalizeOpeningOnWall
} from 'utils/opening-position';
import {
  OpeningOnWallType,
  OpeningRunBoundsType
} from 'utils/opening-position-types';
import {
  freeOpeningOffsetOnRun,
  occupiedOpeningSpans
} from 'utils/opening-free-slot';
import { numericOpeningPosition } from 'utils/opening-position-value';
import {
  assignWallsToPolygonEdges,
  desiredWallForPolygonEdge,
  DesiredPolygonWall,
  matchExistingWallsToDesired,
  wallWidthForLengthFt
} from 'utils/polygon-exterior-walls';
import {
  EdgeCardinal,
  getExteriorPolygonEdges,
  getSpaceVertices,
  SpacePolygonEdge,
  verticesToAbsolute
} from 'utils/space-polygon';
import {
  InteriorPolygonEdgeType,
  OrphanedOpeningType,
  ReconcileWallsInputType,
  ReconcileWallsResultType,
  ReconcileWallType,
  SpaceWallGeometryType,
  WallOpeningPoolType,
  WallRunBoundsResolverType,
  WallRunCandidateType,
  WallRunType
} from 'utils/space-wall-reconcile-types';
import {
  nearestRunToPoint,
  runMidpoint,
  sharedEndpointDisplacementFt
} from 'utils/wall-run-proximity';
import { NearestWallRunType } from 'utils/wall-run-proximity-types';
import {
  pointAlongSegment,
  projectPointOnWallSegment,
  segmentsAreCollinear,
  segmentsCollinearOverlapFt,
  translateWallSegment,
  unclampedOffsetOnSegment,
  wallSegmentFromEdge,
  wallSegmentFromEndpoints
} from 'utils/wall-segment';
import { WallSegmentType } from 'utils/wall-segment-types';

const RUN_EPS = 0.01;
const OFFSET_PRECISION = 1e6;

function roundOffsetFt(value: number): number {
  return Math.round(value * OFFSET_PRECISION) / OFFSET_PRECISION;
}
const DELTA_PRECISION = 1000;

type PlacedOpeningType<O extends OpeningOnWallType> = {
  runIndex: number | null;
  opening: O;
};

function absoluteGeometryVertices(
  geometry: SpaceWallGeometryType
): PositionType[] {
  if (!geometry.position) {
    return [];
  }
  return verticesToAbsolute(getSpaceVertices(geometry), geometry.position);
}

function runFromEdge(edge: SpacePolygonEdge): WallRunType | null {
  if (!edge.cardinal) {
    return null;
  }
  const segment = wallSegmentFromEdge(edge);
  if (segment.lengthFt <= 0) {
    return null;
  }
  return {
    cardinal: edge.cardinal,
    segment,
    lengthFt: segment.lengthFt,
    edgeIndex: edge.edgeIndex
  };
}

export function exteriorWallRuns(
  geometry: SpaceWallGeometryType,
  footprints: FootprintPolygonsType
): { edges: SpacePolygonEdge[]; runs: WallRunType[] } {
  const edges = getExteriorPolygonEdges(
    absoluteGeometryVertices(geometry),
    footprints
  );
  const runs: WallRunType[] = [];
  edges.forEach((edge) => {
    const run = edge.isExterior ? runFromEdge(edge) : null;
    if (run) {
      runs.push(run);
    }
  });
  return { edges, runs };
}

export function wallWidthForRun(run: WallRunType): number {
  return wallWidthForLengthFt(run.lengthFt);
}

function desiredWallForRun(
  run: WallRunType,
  homeOrientation: ExposureDirectionType | undefined
): DesiredPolygonWall {
  return {
    cardinal: run.cardinal,
    width: wallWidthForRun(run),
    exposure: exposureForOutwardNormal(
      run.segment.outwardNormal,
      homeOrientation
    )
  };
}

function isInteriorEdgeWithCardinal(
  edge: SpacePolygonEdge
): edge is InteriorPolygonEdgeType {
  return !edge.isExterior && edge.cardinal !== null;
}

function strandedWallRuns<T extends ReconcileWallType>(
  edges: SpacePolygonEdge[],
  strandedWalls: T[],
  getCardinal: (wall: T) => EdgeCardinal | undefined,
  homeOrientation: ExposureDirectionType | undefined
): Map<string, WallRunType> {
  const runsByWall = new Map<string, WallRunType>();
  if (strandedWalls.length === 0) {
    return runsByWall;
  }
  const interiorEdges = edges.filter(isInteriorEdgeWithCardinal);
  const desired = interiorEdges.map((edge) =>
    desiredWallForPolygonEdge(edge, edge.cardinal, homeOrientation)
  );
  const { matches } = matchExistingWallsToDesired(
    strandedWalls,
    desired,
    getCardinal
  );
  matches.forEach((match, index) => {
    const run = match.existing ? runFromEdge(interiorEdges[index]) : null;
    if (match.existing && run) {
      runsByWall.set(match.existing.uniqueId, run);
    }
  });
  return runsByWall;
}

export function previousRunsByWall<T extends ReconcileWallType>(
  walls: T[],
  previousGeometry: SpaceWallGeometryType | null | undefined,
  footprints: FootprintPolygonsType,
  getCardinal: (wall: T) => EdgeCardinal | undefined,
  homeOrientation: ExposureDirectionType | undefined
): Map<string, WallRunType> {
  const runsByWall = new Map<string, WallRunType>();
  if (!previousGeometry?.position) {
    return runsByWall;
  }
  const { edges } = exteriorWallRuns(previousGeometry, footprints);
  const assigned = assignWallsToPolygonEdges(
    edges,
    walls,
    getCardinal,
    homeOrientation
  );
  assigned.forEach((wall, index) => {
    const run = wall ? runFromEdge(edges[index]) : null;
    if (wall && run) {
      runsByWall.set(wall.uniqueId, run);
    }
  });

  const strandedWalls = walls.filter((wall) => !runsByWall.has(wall.uniqueId));
  strandedWallRuns(edges, strandedWalls, getCardinal, homeOrientation).forEach(
    (run, wallId) => {
      runsByWall.set(wallId, run);
    }
  );
  return runsByWall;
}

function roundDelta(value: number): number {
  return Math.round(value * DELTA_PRECISION) / DELTA_PRECISION;
}

function deltaMagnitude(delta: PositionType): number {
  return Math.hypot(delta.x, delta.y);
}

export function detectTranslation(
  previous: PositionType[],
  next: PositionType[]
): PositionType {
  const votes = new Map<string, { delta: PositionType; count: number }>();
  previous.forEach((from) => {
    next.forEach((to) => {
      const delta = {
        x: roundDelta(to.x - from.x),
        y: roundDelta(to.y - from.y)
      };
      const key = `${delta.x},${delta.y}`;
      const entry = votes.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        votes.set(key, { delta, count: 1 });
      }
    });
  });

  let best: { delta: PositionType; count: number } | null = null;
  for (const entry of votes.values()) {
    const isBetter =
      !best ||
      entry.count > best.count ||
      (entry.count === best.count &&
        deltaMagnitude(entry.delta) < deltaMagnitude(best.delta));
    if (isBetter) {
      best = entry;
    }
  }
  return best ? best.delta : { x: 0, y: 0 };
}

export function translateRun(
  run: WallRunType,
  delta: PositionType
): WallRunType {
  return { ...run, segment: translateWallSegment(run.segment, delta) };
}

function runsCollinear(a: WallRunType, b: WallRunType): boolean {
  return segmentsAreCollinear(a.segment, b.segment);
}

function runOverlap(a: WallRunType, b: WallRunType): number {
  return segmentsCollinearOverlapFt(a.segment, b.segment);
}

function openingSegmentOnRun<O extends OpeningOnWallType>(
  run: WallRunType,
  opening: O,
  position: number
): WallSegmentType {
  return wallSegmentFromEndpoints(
    pointAlongSegment(run.segment, position),
    pointAlongSegment(run.segment, position + opening.width),
    {
      cardinal: run.cardinal,
      outwardNormal: run.segment.outwardNormal,
      edgeIndex: run.edgeIndex
    }
  );
}

function openingOffsetOnRun(
  run: WallRunType,
  openingSegment: WallSegmentType
): number {
  return roundOffsetFt(
    Math.min(
      unclampedOffsetOnSegment(run.segment, openingSegment.start),
      unclampedOffsetOnSegment(run.segment, openingSegment.end)
    )
  );
}

function nearestWidthWall<T extends ReconcileWallType>(
  candidates: T[],
  width: number
): T | null {
  let best: T | null = null;
  let bestDiff = Infinity;
  for (const wall of candidates) {
    const diff = Math.abs(wall.width - width);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = wall;
    }
  }
  return best;
}

function collinearOverlapScore(
  previousRun: WallRunType,
  run: WallRunType
): number | null {
  const overlap = runOverlap(previousRun, run);
  return overlap > RUN_EPS ? overlap : null;
}

function previousRunCandidates<T extends ReconcileWallType>(
  runs: WallRunType[],
  walls: T[],
  previousRuns: Map<string, WallRunType>,
  score: (previousRun: WallRunType, run: WallRunType) => number | null
): WallRunCandidateType<T>[] {
  const candidates: WallRunCandidateType<T>[] = [];
  runs.forEach((run, runIndex) => {
    walls.forEach((wall) => {
      const previousRun = previousRuns.get(wall.uniqueId);
      const value = previousRun ? score(previousRun, run) : null;
      if (value !== null) {
        candidates.push({ runIndex, wall, score: value });
      }
    });
  });
  return candidates;
}

export function matchWallsToRuns<T extends ReconcileWallType>(
  runs: WallRunType[],
  desired: DesiredPolygonWall[],
  walls: T[],
  previousRuns: Map<string, WallRunType>,
  getCardinal: (wall: T) => EdgeCardinal | undefined
): { assigned: Array<T | null>; unmatched: T[] } {
  const assigned: Array<T | null> = runs.map(() => null);
  const usedWallIds = new Set<string>();
  const assignRanked = (candidates: WallRunCandidateType<T>[]) => {
    candidates.forEach(({ runIndex, wall }) => {
      if (assigned[runIndex] === null && !usedWallIds.has(wall.uniqueId)) {
        assigned[runIndex] = wall;
        usedWallIds.add(wall.uniqueId);
      }
    });
  };

  assignRanked(
    previousRunCandidates(
      runs,
      walls,
      previousRuns,
      collinearOverlapScore
    ).sort((a, b) => b.score - a.score)
  );

  assignRanked(
    previousRunCandidates(
      runs,
      walls,
      previousRuns,
      sharedEndpointDisplacementFt
    ).sort((a, b) => a.score - b.score)
  );

  runs.forEach((run, runIndex) => {
    if (assigned[runIndex] === null) {
      const fallback = nearestWidthWall(
        walls.filter(
          (wall) =>
            !usedWallIds.has(wall.uniqueId) &&
            getCardinal(wall) === run.cardinal
        ),
        desired[runIndex].width
      );
      if (fallback) {
        assigned[runIndex] = fallback;
        usedWallIds.add(fallback.uniqueId);
      }
    }
  });

  return {
    assigned,
    unmatched: walls.filter((wall) => !usedWallIds.has(wall.uniqueId))
  };
}

function collinearRunIndexForOpening(
  runs: WallRunType[],
  previousRun: WallRunType,
  openingSegment: WallSegmentType
): number {
  let bestIndex = -1;
  let bestOverlap = RUN_EPS;
  runs.forEach((run, index) => {
    if (runsCollinear(run, previousRun)) {
      const overlap = segmentsCollinearOverlapFt(run.segment, openingSegment);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestIndex = index;
      }
    }
  });
  return bestIndex;
}

function openingCenterOnRun<O extends OpeningOnWallType>(
  run: WallRunType,
  opening: O,
  position: number
): PositionType {
  return pointAlongSegment(run.segment, position + opening.width / 2);
}

function previousOpeningCenter<O extends OpeningOnWallType>(
  previousRun: WallRunType,
  opening: O
): PositionType {
  const position = numericOpeningPosition(opening.positionOnWall);
  if (position === undefined) {
    return runMidpoint(previousRun);
  }
  return openingCenterOnRun(previousRun, opening, position);
}

function projectedOpeningOffset(
  run: WallRunType,
  center: PositionType,
  width: number,
  bounds: OpeningRunBoundsType
): number {
  const { offsetFt } = projectPointOnWallSegment(run.segment, center);
  return clampOpeningOffsetOnRun(
    roundOffsetFt(offsetFt - width / 2),
    width,
    bounds
  );
}

export function placeOpeningOnRuns<O extends OpeningOnWallType>(
  opening: O,
  previousRun: WallRunType | undefined,
  runs: WallRunType[],
  ownerRunIndex: number | null,
  boundsForRun: WallRunBoundsResolverType
): PlacedOpeningType<O> {
  const position = numericOpeningPosition(opening.positionOnWall);
  if (previousRun && position !== undefined) {
    const openingSegment = openingSegmentOnRun(previousRun, opening, position);
    const collinearIndex = collinearRunIndexForOpening(
      runs,
      previousRun,
      openingSegment
    );
    if (collinearIndex !== -1) {
      const run = runs[collinearIndex];
      return {
        runIndex: collinearIndex,
        opening: {
          ...opening,
          positionOnWall: clampOpeningOffsetOnRun(
            openingOffsetOnRun(run, openingSegment),
            opening.width,
            boundsForRun(run)
          )
        }
      };
    }
    if (ownerRunIndex !== null) {
      const run = runs[ownerRunIndex];
      return {
        runIndex: ownerRunIndex,
        opening: {
          ...opening,
          positionOnWall: projectedOpeningOffset(
            run,
            openingCenterOnRun(previousRun, opening, position),
            opening.width,
            boundsForRun(run)
          )
        }
      };
    }
  }

  if (ownerRunIndex !== null) {
    return {
      runIndex: ownerRunIndex,
      opening: normalizeOpeningOnWall(
        opening,
        boundsForRun(runs[ownerRunIndex])
      )
    };
  }

  return { runIndex: null, opening };
}

function placeOrphanOnRun<T extends ReconcileWallType>(
  orphan: OrphanedOpeningType,
  nearest: NearestWallRunType,
  runs: WallRunType[],
  nextWalls: T[],
  runBounds: WallRunBoundsResolverType
): void {
  const wall = nextWalls[nearest.runIndex];
  const positionOnWall = freeOpeningOffsetOnRun(
    roundOffsetFt(nearest.offsetFt - orphan.opening.width / 2),
    orphan.opening.width,
    runBounds(runs[nearest.runIndex]),
    occupiedOpeningSpans([
      ...(wall.verticalGlass ?? []),
      ...(wall.solidDoors ?? [])
    ])
  );
  if (orphan.kind === 'window') {
    wall.verticalGlass = [
      ...(wall.verticalGlass ?? []),
      { ...orphan.opening, positionOnWall }
    ];
  } else {
    wall.solidDoors = [
      ...(wall.solidDoors ?? []),
      { ...orphan.opening, positionOnWall }
    ];
  }
}

export function redistributeWallItems<T extends ReconcileWallType>(
  items: WallOpeningPoolType,
  walls: T[],
  runBoundsForTargetWall: (wall: T) => OpeningRunBoundsType
): void {
  if (
    (items.windows.length === 0 && items.doors.length === 0) ||
    walls.length === 0
  ) {
    return;
  }

  const totalItems = items.windows.length + items.doors.length;
  const distributionPerWall = Math.ceil(totalItems / walls.length);
  let itemIndex = 0;
  let wallIndex = 0;

  const advance = () => {
    itemIndex += 1;
    if (itemIndex >= distributionPerWall) {
      itemIndex = 0;
      wallIndex += 1;
    }
  };

  items.windows.forEach((window) => {
    if (wallIndex < walls.length) {
      const wall = walls[wallIndex];
      wall.verticalGlass = [
        ...(wall.verticalGlass || []),
        normalizeOpeningOnWall(window, runBoundsForTargetWall(wall))
      ];
      advance();
    }
  });

  items.doors.forEach((door) => {
    if (wallIndex < walls.length) {
      const wall = walls[wallIndex];
      wall.solidDoors = [
        ...(wall.solidDoors || []),
        normalizeOpeningOnWall(door, runBoundsForTargetWall(wall))
      ];
      advance();
    }
  });
}

function wallForRun<T extends ReconcileWallType>(
  existing: T | null,
  desired: DesiredPolygonWall,
  isBasement: boolean,
  createWall: (desired: DesiredPolygonWall) => T
): T {
  if (!existing) {
    return { ...createWall(desired), verticalGlass: [], solidDoors: [] };
  }
  const next: T = { ...existing, verticalGlass: [], solidDoors: [] };
  if (!isBasement) {
    (next as AboveGradeWallType).edge = desired.cardinal;
  }
  if (!next.modified) {
    next.width = desired.width;
    next.exposure = desired.exposure;
  }
  return next;
}

export function reconcileWallsForGeometry<T extends ReconcileWallType>(
  input: ReconcileWallsInputType<T>
): ReconcileWallsResultType<T> {
  const {
    walls,
    previousGeometry,
    nextGeometry,
    footprints,
    homeOrientation,
    getCardinal,
    createWall,
    isBasement,
    runBounds
  } = input;
  const { runs } = exteriorWallRuns(nextGeometry, footprints);
  const desired = runs.map((run) => desiredWallForRun(run, homeOrientation));

  const delta = previousGeometry?.position
    ? detectTranslation(
        absoluteGeometryVertices(previousGeometry),
        absoluteGeometryVertices(nextGeometry)
      )
    : { x: 0, y: 0 };
  const previousRuns = new Map<string, WallRunType>();
  previousRunsByWall(
    walls,
    previousGeometry,
    footprints,
    getCardinal,
    homeOrientation
  ).forEach((run, wallId) => {
    previousRuns.set(wallId, translateRun(run, delta));
  });

  const { assigned, unmatched } = matchWallsToRuns(
    runs,
    desired,
    walls,
    previousRuns,
    getCardinal
  );

  const nextWalls = runs.map((_, runIndex) =>
    wallForRun(assigned[runIndex], desired[runIndex], isBasement, createWall)
  );
  const ownerRunIndexByWallId = new Map<string, number>();
  assigned.forEach((wall, runIndex) => {
    if (wall) {
      ownerRunIndexByWallId.set(wall.uniqueId, runIndex);
    }
  });

  const pool: WallOpeningPoolType = { windows: [], doors: [] };
  const orphans: OrphanedOpeningType[] = [];
  walls.forEach((wall) => {
    const ownerRunIndex = ownerRunIndexByWallId.get(wall.uniqueId) ?? null;
    const previousRun = previousRuns.get(wall.uniqueId);
    (wall.verticalGlass ?? []).forEach((glass) => {
      const placed = placeOpeningOnRuns(
        glass,
        previousRun,
        runs,
        ownerRunIndex,
        runBounds
      );
      if (placed.runIndex !== null) {
        nextWalls[placed.runIndex].verticalGlass.push(
          placed.opening as GlassItemType
        );
      } else if (previousRun) {
        orphans.push({
          kind: 'window',
          opening: glass,
          previousCenter: previousOpeningCenter(previousRun, glass)
        });
      } else {
        pool.windows.push(glass);
      }
    });
    (wall.solidDoors ?? []).forEach((door) => {
      const placed = placeOpeningOnRuns(
        door,
        previousRun,
        runs,
        ownerRunIndex,
        runBounds
      );
      if (placed.runIndex !== null) {
        nextWalls[placed.runIndex].solidDoors.push(
          placed.opening as DoorItemType
        );
      } else if (previousRun) {
        orphans.push({
          kind: 'door',
          opening: door,
          previousCenter: previousOpeningCenter(previousRun, door)
        });
      } else {
        pool.doors.push(door);
      }
    });
  });

  orphans.forEach((orphan) => {
    const nearest = nearestRunToPoint(runs, orphan.previousCenter);
    if (nearest) {
      placeOrphanOnRun(orphan, nearest, runs, nextWalls, runBounds);
    }
  });

  redistributeWallItems(pool, nextWalls, (wall) => {
    const run = runs[nextWalls.indexOf(wall)];
    return run ? runBounds(run) : EMPTY_OPENING_RUN_BOUNDS;
  });

  return {
    walls: nextWalls,
    removedWallIds: unmatched.map((wall) => wall.uniqueId)
  };
}
